// ============================================
// pages/ProjectDetail.tsx
//
// 项目详情页. 接后端 (Week 2 已就绪):
//   community-get-project          → 项目信息
//   community-get-public-profile   → 作者卡 (get-project 只返 author_user_id, 这里用项目作者解析)
//   community-like-project         → 点赞 toggle
//   community-collect-project      → 收藏 toggle
//   community-check-download       → 下载前置检查 (登录与下载权限)
//   community-execute-download     → 真正取下载链接
//   community-list-comments        → 评论列表
//   community-comment-project      → 发评论
//
// Week 2 重做要点 (官网设计系统):
//   - 套 Layout (窄容器)
//   - 两栏: 左主体 (封面/简介/积木预览/评论) + 右侧栏 (互动 + 作者 + 数据)
//   - 互动按钮乐观更新; 未登录给明确提示而非静默失败
//   - BlocklyViewer 暂占位 (get-project 尚未返 workspace_xml, 接通后传 xml 即可)
//
// 已知契约现实:
//   - get-project 不返 workspace_xml / liked / collected / 作者昵称
//   - 故 liked/collected 初始未知, 由用户首次 toggle 的返回确定; 作者另查 profile
// ============================================

import { useEffect, useState } from 'react';
import { Search, Puzzle, MessageCircle, Heart, Bookmark, User, Plug, ArrowLeft, Clock, Pencil, Trash2 } from 'lucide-react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorPage from '../components/ErrorPage';
import Avatar from '../components/Avatar';
import BlocklyViewer from '../components/BlocklyViewer';
import CommentSection from '../components/CommentSection';
import ReportModal from '../components/ReportModal';
import { callApi } from '../services/api';
import { authedCallApi, getCachedUser } from '../services/auth';
import { requireLogin } from '../services/loginPrompt';
import { categoryLabel } from '../services/categories';
import type { ProjectPublic } from '../types/community';

const DIFFICULTY_LABEL: Record<string, string> = {
  beginner: '入门', intermediate: '进阶', advanced: '高级',
};
const POLICY_LABEL: Record<string, string> = {
  public_view_only: '仅展示', free_download: '免费下载', paid_download: '免费下载',
};

interface AuthorCard {
  short_id: string;
  nickname: string;
  avatar_url: string | null;
  level: number;
  role?: string;
}

export default function ProjectDetail() {
  const { shortId } = useParams<{ shortId: string }>();
  const navigate = useNavigate();
  // 嵌入/纯内容模式: hash 带 embed=1 (审核预览用 iframe 嵌入时, 只显示作品内容,
  //   隐藏导航/返回/编辑/删除/下载/作者卡/数据/评论区)
  const isEmbed = typeof window !== 'undefined' && /[?&]embed=1\b/.test(window.location.hash + window.location.search);
  // 审核预览令牌: admin 在审核抽屉里嵌入时, 通过 URL 传 admin token,
  //   使 getProject 能放行 hidden/pending/rejected 等未公开状态的作品
  const previewToken = (() => {
    if (typeof window === 'undefined') return '';
    const m = (window.location.hash + window.location.search).match(/[?&]preview_token=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  })();
  const [project, setProject] = useState<ProjectPublic | null>(null);
  const [author, setAuthor] = useState<AuthorCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showReport, setShowReport] = useState(false);

  // 互动态 (初始未知; toggle 返回后确定)
  const [liked, setLiked] = useState(false);
  const [collected, setCollected] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [collectCount, setCollectCount] = useState(0);
  const [busy, setBusy] = useState<'like' | 'collect' | 'download' | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!shortId) return;
    setLoading(true);
    setError(null);
    callApi<{ project: ProjectPublic }>('community-get-project', previewToken ? { short_id: shortId, token: previewToken } : { short_id: shortId })
      .then(async (r) => {
        if (!r.ok || !r.project) {
          setError(r.message || r.error || '项目不存在');
          return;
        }
        const p = r.project as ProjectPublic;
        setProject(p);
        setLikeCount(p.likes_count);
        setCollectCount(p.collections_count);

        // 作者卡: 后端 get-project 已补 author 字段(昵称/头像/short_id/level)
        const a = (p as ProjectPublic & { author?: AuthorCard }).author;
        if (a) setAuthor(a);
      })
      .finally(() => setLoading(false));
  }, [shortId]);

  function flash(msg: string) {
    setToast(msg);
    window.clearTimeout((flash as any)._t);
    (flash as any)._t = window.setTimeout(() => setToast(null), 2600);
  }

  async function onLike() {
    if (!project || busy) return;
    if (!requireLogin()) return;
    setBusy('like');
    // 乐观
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => c + (next ? 1 : -1));
    const r = await authedCallApi<{ liked: boolean }>('community-like-project', {
      project_short_id: project.short_id,
      op: 'toggle',
    });
    setBusy(null);
    if (!r.ok) {
      // 回滚
      setLiked(!next);
      setLikeCount((c) => c + (next ? -1 : 1));
      flash(r.error === 'NO_TOKEN' ? '请先登录后再点赞' : (r.message || '操作失败'));
      return;
    }
    if (typeof r.liked === 'boolean') setLiked(r.liked);
  }

  async function onCollect() {
    if (!project || busy) return;
    if (!requireLogin()) return;
    setBusy('collect');
    const next = !collected;
    setCollected(next);
    setCollectCount((c) => c + (next ? 1 : -1));
    const r = await authedCallApi<{ collected: boolean }>('community-collect-project', {
      project_short_id: project.short_id,
      op: 'toggle',
    });
    setBusy(null);
    if (!r.ok) {
      setCollected(!next);
      setCollectCount((c) => c + (next ? -1 : 1));
      flash(r.error === 'NO_TOKEN' ? '请先登录后再收藏' : (r.message || '操作失败'));
      return;
    }
    if (typeof r.collected === 'boolean') setCollected(r.collected);
  }

  async function onDownload() {
    if (!project || busy) return;
    if (!requireLogin()) return;
    if (project.download_policy === 'public_view_only') {
      flash('该作品仅供浏览,不可下载');
      return;
    }
    setBusy('download');
    // 1) 前置检查
    const chk = await authedCallApi<{ allowed: boolean; will_cost_points: number }>(
      'community-check-download',
      { project_short_id: project.short_id },
    );
    if (!chk.ok || !chk.allowed) {
      setBusy(null);
      flash(chk.error === 'NO_TOKEN' ? '请先登录后再下载' : (chk.message || '暂不可下载'));
      return;
    }
    // 2) 免费下载 Studio 原生工程
    const exec = await authedCallApi<{ download_url: string; download_filename?: string }>('community-execute-download', {
      project_short_id: project.short_id,
    });
    setBusy(null);
    if (!exec.ok || !exec.download_url) {
      flash(exec.message || exec.error || '下载失败');
      return;
    }
    // 用 <a download> 触发,使用服务端返回的 Studio 原生工程文件名
    const a = document.createElement('a');
    a.href = exec.download_url as string;
    a.download = (exec.download_filename as string) || `${project.title || project.short_id}.hpd`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    flash('开始下载…');
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !project) {
    return (
      <ErrorPage icon={<Search className="w-12 h-12" strokeWidth={1.5} />} title="项目不存在" message={error || '可能已被删除或链接错误。'} fullscreenGradient={false} />
    );
  }

  const canDownload = project.download_policy !== 'public_view_only';

  // 作者本人?(当前登录用户 == 作品作者)
  const currentUser = getCachedUser();
  const isOwner = !!currentUser && currentUser.user_id === project.author_user_id;

  const handleDelete = async () => {
    setDeleting(true);
    const res = await authedCallApi<{ deleted: boolean }>(
      'community-delete-project',
      { short_id: project.short_id },
    );
    setDeleting(false);
    if (res.ok) {
      // 删除后回作者个人主页(而非论坛首页)
      if (currentUser && currentUser.short_id) navigate(`/u/${currentUser.short_id}`, { replace: true });
      else navigate('/', { replace: true });
    } else {
      setShowDelete(false);
      setToast(res.message || res.error || '删除失败');
    }
  };

  return (
    <Layout width="wide" embed={isEmbed} showSearch={!isEmbed}>
      {/* 返回键 + 作者操作 (embed 模式隐藏) */}
      {!isEmbed && (
      <div className="flex items-center justify-between mt-6">
        <button
          onClick={() => {
            // 有浏览器历史就退,没有(直接打开链接)就回首页
            if (window.history.length > 1) navigate(-1);
            else navigate('/');
          }}
          className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-gold-600 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" strokeWidth={2} />
          返回
        </button>

        {isOwner && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/edit/${project.short_id}`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-ink-600 border border-ink-300/60 rounded-pill hover:border-gold-400 hover:text-gold-600 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" strokeWidth={2} /> 编辑
            </button>
            <button
              onClick={() => setShowDelete(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-pill hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={2} /> 删除
            </button>
          </div>
        )}
      </div>
      )}

      {/* toast */}
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-ink-900 text-white text-sm px-4 py-2.5 rounded-xl shadow-card animate-[fadeIn_.2s_ease]">
          {toast}
        </div>
      )}

      {/* 审核中横幅 */}
      {!isEmbed && project.status === 'pending_audit' && (
        <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4">
          <div className="flex items-center gap-2 text-amber-800 font-semibold">
            <Clock className="w-4 h-4" strokeWidth={2} /> 你的作品正在审核中
          </div>
          <p className="text-sm text-amber-700 mt-1 ml-6">
            审核通过后将出现在社区首页。审核期间其他用户暂时看不到。
          </p>
        </div>
      )}

      {/* 复查中横幅 (管理员二次抽检撤下 → hidden) */}
      {!isEmbed && project.status === 'hidden' && (
        <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4">
          <div className="flex items-center gap-2 text-amber-800 font-semibold">
            <Clock className="w-4 h-4" strokeWidth={2} /> 你的作品正在复查中
          </div>
          <p className="text-sm text-amber-700 mt-1 ml-6">
            该作品已暂时从社区下架复查,复查期间其他用户看不到。复查通过后会重新展示。
          </p>
        </div>
      )}

      {/* 被拒横幅 (rejected) */}
      {!isEmbed && project.status === 'rejected' && (
        <div className="mt-4 rounded-2xl bg-red-50 border border-red-200 px-5 py-4">
          <div className="flex items-center gap-2 text-red-700 font-semibold">
            <Trash2 className="w-4 h-4" strokeWidth={2} /> 该作品未通过审核
          </div>
          <p className="text-sm text-red-600 mt-1 ml-6">
            作品未在社区展示,请修改后重新发布。
          </p>
        </div>
      )}

      <div className={isEmbed ? "grid grid-cols-1 gap-8 py-8" : "grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 py-8"}>
        {/* ─────────── 左:主体 ─────────── */}
        <article className="min-w-0">
          {/* 封面 */}
          <div className="rounded-2xl overflow-hidden border border-ink-300/40 bg-gradient-to-br from-gold-50 to-gold-100 aspect-[16/9]">
            {project.cover_url ? (
              <img
                src={project.cover_url}
                alt={project.title}
                className="w-full h-full object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div className="w-full h-full grid place-items-center text-gold-200"><Puzzle className="w-16 h-16" strokeWidth={1.5} /></div>
            )}
          </div>

          {/* 标题 + 元信息 */}
          <h1 className="text-2xl sm:text-3xl font-black text-ink-900 mt-6 leading-tight">
            {project.title}
          </h1>
          <div className="flex items-center gap-2 flex-wrap mt-3">
            <span className="chip">{categoryLabel(project.category) || project.category}</span>
            <span className="chip">{project.board_type}</span>
            <span className="chip">{project.workspace_xml ? `${project.block_count} 积木` : '代码作品'}</span>
            <span className="chip">{DIFFICULTY_LABEL[project.difficulty] || project.difficulty}</span>
          </div>

          {/* 标签 */}
          {project.tags?.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mt-3">
              {project.tags.map((t) => (
                <span key={t} className="ex-tag">#{t}</span>
              ))}
            </div>
          )}

          {/* 简介 */}
          <section className="mt-7">
            <SectionTitle num="01" title="作品简介" />
            <p className="text-ink-700 leading-relaxed whitespace-pre-wrap mt-3">
              {project.description}
            </p>
          </section>

          {/* 教程 (有才显示) */}
          {project.tutorial_md?.trim() && (
            <section className="mt-8">
              <SectionTitle num="02" title="制作教程" />
              <div className="text-ink-700 leading-relaxed whitespace-pre-wrap mt-3 bg-white border border-ink-300/40 rounded-2xl p-5">
                {project.tutorial_md}
              </div>
            </section>
          )}

          {/* 积木预览 */}
          <section className="mt-8">
            <SectionTitle num={project.tutorial_md?.trim() ? '03' : '02'} title={project.workspace_xml ? '积木工作区' : '项目代码'} />
            <div className="mt-3">
              {/* C-积木预览: get-project 返回 workspace_xml,只读渲染 */}
              {project.workspace_xml ? (
                <BlocklyViewer xml={project.workspace_xml} boardType={project.board_type} />
              ) : (
                <pre className="bg-white border border-ink-300/40 rounded-2xl p-5 text-sm text-ink-700 overflow-auto max-h-[480px]" tabIndex={0} aria-label="项目代码">
                  <code>{project.code || '暂无代码'}</code>
                </pre>
              )}
            </div>
          </section>

          {/* 硬件清单 */}
          {project.hardware_list?.length > 0 && (
            <section className="mt-8">
              <SectionTitle num={project.tutorial_md?.trim() ? '04' : '03'} title="用到的硬件" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                {project.hardware_list.map((h) => (
                  <div key={h.id} className="flex items-center gap-3 bg-white border border-ink-300/40 rounded-xl p-3">
                    <span className="feat-ic !w-10 !h-10 text-gold-500"><Plug className="w-4 h-4" strokeWidth={2} /></span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-ink-900 truncate">{h.name}</div>
                      <div className="text-xs text-ink-400">{h.source}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 评论区 (embed 模式隐藏) */}
          {!isEmbed && (
          <section className="mt-10">
            <SectionTitle num={<MessageCircle className="w-4 h-4" strokeWidth={2} />} title={`评论 ${project.comments_count > 0 ? `· ${project.comments_count}` : ''}`} />
            <div className="mt-4">
              <CommentSection projectShortId={project.short_id} onPosted={(counted) => {
                flash(counted ? '评论已提交' : '评论已提交, 审核通过后显示');
                if (counted) setProject((p) => p ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p);
              }} />
            </div>
          </section>
          )}
        </article>

        {/* ─────────── 右:侧栏 (embed 模式隐藏) ─────────── */}
        {!isEmbed && (
        <aside className="lg:sticky lg:top-20 lg:self-start space-y-5">
          {/* 互动按钮 */}
          <div className="bg-white border border-ink-300/40 rounded-2xl p-5 shadow-card">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={onLike}
                disabled={busy === 'like'}
                className={`flex flex-col items-center gap-1 py-3 rounded-xl border transition-colors ${
                  liked ? 'bg-gold-soft border-gold-200 text-gold-700' : 'border-ink-300/40 text-ink-600 hover:border-gold-200'
                }`}
              >
                <Heart className="w-5 h-5" strokeWidth={2} fill={liked ? 'currentColor' : 'none'} />
                <span className="text-xs font-semibold tabular-nums">{likeCount}</span>
                <span className="text-[11px] text-ink-400">点赞</span>
              </button>
              <button
                onClick={onCollect}
                disabled={busy === 'collect'}
                className={`flex flex-col items-center gap-1 py-3 rounded-xl border transition-colors ${
                  collected ? 'bg-gold-soft border-gold-200 text-gold-700' : 'border-ink-300/40 text-ink-600 hover:border-gold-200'
                }`}
              >
                <Bookmark className="w-5 h-5" strokeWidth={2} fill={collected ? 'currentColor' : 'none'} />
                <span className="text-xs font-semibold tabular-nums">{collectCount}</span>
                <span className="text-[11px] text-ink-400">收藏</span>
              </button>
            </div>

            <button
              onClick={onDownload}
              disabled={busy === 'download' || !canDownload}
              className="btn-primary w-full mt-3"
            >
              {busy === 'download'
                ? '处理中…'
                : !canDownload
                ? '仅供浏览'
                : `免费下载 ${project.workspace_xml ? '.hpd' : '.ino'}`}
            </button>
            <p className="text-[11px] text-ink-400 text-center mt-2">
              {POLICY_LABEL[project.download_policy]} · {fmtSize(project.hpd_file_size)}
            </p>
          </div>

          {/* 作者卡 */}
          <div className="bg-white border border-ink-300/40 rounded-2xl p-5">
            <div className="section-num mb-3">AUTHOR</div>
            {author ? (
              <Link to={`/u/${author.short_id}`} className="flex items-center gap-3 group">
                <Avatar name={author.nickname} size={48} className="!rounded-full border border-ink-300/50" />
                <div className="min-w-0">
                  <div className="font-semibold text-ink-900 group-hover:text-gold-600 transition-colors truncate">
                    {author.nickname}
                  </div>
                  <div className="text-xs text-ink-400">LV{author.level}</div>
                </div>
              </Link>
            ) : (
              <div className="flex items-center gap-3">
                <span className="grid place-items-center w-12 h-12 rounded-full bg-gold-100 text-gold-600"><User className="w-6 h-6" strokeWidth={2} /></span>
                <div className="text-sm text-ink-400">创作者</div>
              </div>
            )}
          </div>

          {/* 数据 */}
          <div className="bg-white border border-ink-300/40 rounded-2xl p-5">
            <div className="section-num mb-3">STATS</div>
            <div className="grid grid-cols-3 gap-y-4 text-center">
              <Stat label="浏览" value={project.views_count} />
              <Stat label="点赞" value={likeCount} />
              <Stat label="收藏" value={collectCount} />
              <Stat label="评论" value={project.comments_count} />
              <Stat label="下载" value={project.downloads_count} />
              <Stat label="复刻" value={project.forks_count} />
            </div>
          </div>

          {/* 举报 (非作者本人才显示) */}
          {!isOwner && (
            <button
              onClick={() => setShowReport(true)}
              className="w-full text-xs text-ink-400 hover:text-red-500 transition-colors py-2"
            >
              举报此作品
            </button>
          )}
        </aside>
        )}
      </div>

      {/* 删除确认弹层 */}
      {showDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => !deleting && setShowDelete(false)}
        >
          <div
            className="bg-white rounded-3xl shadow-card p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-3">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-red-50 text-red-500">
                <Trash2 className="w-6 h-6" strokeWidth={1.5} />
              </span>
            </div>
            <h3 className="text-lg font-bold text-ink-900 text-center mb-2">删除这个作品?</h3>
            <p className="text-sm text-ink-500 text-center leading-relaxed mb-6">
              删除后作品将从社区下架,其他人无法再看到。此操作不可撤销。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDelete(false)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl border border-ink-300/60 text-ink-600 text-sm font-medium hover:bg-paper transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? '删除中…' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 举报弹窗 */}
      {showReport && (
        <ReportModal
          targetType="project"
          targetShortId={project.short_id}
          onClose={() => setShowReport(false)}
          onDone={(msg) => flash(msg)}
        />
      )}
    </Layout>
  );
}

function SectionTitle({ num, title }: { num: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="section-num">{num}</span>
      <h2 className="text-lg font-bold text-ink-900">{title}</h2>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xl font-black text-ink-900 tabular-nums">{value}</div>
      <div className="text-xs text-ink-400 mt-0.5">{label}</div>
    </div>
  );
}

function fmtSize(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
